import { Button, Card, Text, VSpacer } from '@/components/DesignSystem';
import { ProgramTags } from '@/components/shared/ProgramTags';
import { getHasTierGraphics } from '@/pages/CustomerDashboard/helpers';
import { TierImage } from '@/pages/CustomerDashboard/TierImage';
import { TierBanner, TierHistoryTimeline } from '@/pages/FarmerList/TierStatusCard';
import { ApiEnergyCustomerProgram } from '@api/interfaces/ApiEnergyCustomer';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TaskAltOutlined from '@mui/icons-material/TaskAltOutlined';
import { Box, Stack, useTheme } from '@mui/material';
import { FarmerTierHistoryType } from '@shared/enums';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface EnergyStatusCardProps {
  energy: ApiEnergyCustomerProgram,
  isReadOnly: boolean,
  onAddOverride: () => void,
  onManageBehaviors: () => void,
}

export const EnergyStatusCard = ({
  energy,
  isReadOnly,
  onAddOverride,
  onManageBehaviors,
}: EnergyStatusCardProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const isComplete = energy.totalCount > 0 && energy.completedCount === energy.totalCount;
  const hasTierGraphics = getHasTierGraphics(energy.tiers.map(({ name }) => name));
  const tierNameByRank = new Map(energy.tiers.map(({ name, rank }) => [rank, name]));
  const historyEntries = energy.tierHistory.map((entry, index) => {
    const type = entry.type as FarmerTierHistoryType;
    const rank = type === FarmerTierHistoryType.Earned ? entry.rankEarned : entry.rankOverride;
    return {
      effectiveDate: entry.effectiveDate,
      endDate: entry.endDate,
      id: `${entry.effectiveDate}-${index}`,
      tierName: rank === null ? undefined : tierNameByRank.get(rank),
      type,
    };
  });

  return (
    <Card testID="energy-status-card">
      <Stack alignItems="center" direction="row" justifyContent="space-between">
        <Text category="overline" textTransform="uppercase">{energy.programName}</Text>
        <ProgramTags isEnergyMember testID="energy-status-card-tag" />
      </Stack>
      <VSpacer size="3" />
      <Box data-testid="energy-status-tier">
        <TierBanner
          image={hasTierGraphics && !!energy.currentTier && (
            <TierImage
              hasTierGraphics
              size={{ height: '30px', width: 'auto' }}
              tier={energy.currentTier}
            />
          )}
          isOverride={energy.isOverride}
          tierName={energy.currentTier?.name ?? t(($) => $.customers.details.notEnrolled)}
        />
      </Box>
      <Text category="overline" sx={{ color: theme.palette.text.secondary }}>
        {t(($) => $.energy.profile.behaviorsTitle)}
      </Text>
      <VSpacer size="1" />
      <Text
        category="label-large"
        sx={{ color: isComplete ? theme.palette.success.main : undefined }}
        testID="energy-behaviors-count"
      >
        {t(($) => $.energy.profile.behaviorsCount, {
          completed: energy.completedCount,
          total: energy.totalCount,
        })}
      </Text>
      <VSpacer size="4" />
      <Box sx={{ borderTop: `1px solid ${theme.palette.outline}` }} />
      <VSpacer size="4" />
      <Stack direction="row" spacing="8px">
        {!isReadOnly && historyEntries.length > 0 && (
          <Button
            color="secondary"
            endIcon={(
              <ExpandMoreIcon
                sx={{
                  transform: expanded ? 'rotate(180deg)' : 'none',
                  transition: 'transform 150ms',
                }}
              />
            )}
            onClick={() => setExpanded(!expanded)}
            startIcon={<CalendarTodayOutlinedIcon />}
            sx={{ flex: 1, minWidth: 0 }}
            testID="energy-status-history-toggle"
            variant="outlined"
          >
            {t(($) => $.customers.details.tierOverride.history)}
          </Button>
        )}
        {!isReadOnly && (
          <Button
            onClick={onAddOverride}
            startIcon={<EditOutlinedIcon />}
            sx={{ flex: 1, minWidth: 0 }}
            testID="energy-status-add-override"
            variant="outlined"
          >
            {t(($) => $.customers.details.tierOverride.addOverride)}
          </Button>
        )}
      </Stack>
      <VSpacer size="3" />
      <Button
        onClick={onManageBehaviors}
        startIcon={<TaskAltOutlined />}
        testID="energy-manage-behaviors"
        variant="contained"
      >
        {isReadOnly
          ? t(($) => $.energy.profile.viewBehaviors)
          : t(($) => $.energy.profile.manageBehaviors)}
      </Button>
      {expanded && historyEntries.length > 0 && (
        <>
          <VSpacer size="7" />
          <TierHistoryTimeline entries={historyEntries} />
        </>
      )}
    </Card>
  );
};
